import React from 'react';
import styled from 'styled-components';
import { Card, Flex, Box, Text, Link, Image, Truncate } from 'pcln-design-system';
import { User } from 'pcln-icons';
import { themeGet } from '@styled-system/theme-get';
import { FlightsOutline } from '@pcln/brand';
const StyledCard = /*#__PURE__*/styled(Card).withConfig({
  displayName: "AbandonedFlight__StyledCard",
  componentId: "sc-gp36r7-0"
})(["&:hover{box-shadow:", ";}"], ({
  theme
}) => theme.shadows.lg);
const StyledFlex = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "AbandonedFlight__StyledFlex",
  componentId: "sc-gp36r7-1"
})(["gap:", ";"], themeGet('space.2'));
const NoWrapDateText = /*#__PURE__*/styled(Text).withConfig({
  displayName: "AbandonedFlight__NoWrapDateText",
  componentId: "sc-gp36r7-2"
})(["", "{white-space:nowrap;}"], themeGet('mediaQueries.md'));
const NoWrapText = /*#__PURE__*/styled(Text).withConfig({
  displayName: "AbandonedFlight__NoWrapText",
  componentId: "sc-gp36r7-3"
})(["", "{white-space:nowrap;}"], themeGet('mediaQueries.lg'));
const IconWrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "AbandonedFlight__IconWrapper",
  componentId: "sc-gp36r7-4"
})(["flex-shrink:0;"]);
const StyledTruncate = /*#__PURE__*/styled(Truncate).withConfig({
  displayName: "AbandonedFlight__StyledTruncate",
  componentId: "sc-gp36r7-5"
})(["white-space:normal;-webkit-line-clamp:2;-webkit-box-orient:vertical;display:-webkit-box;"]);
const tripTypeText = {
  ROUND_TRIP: 'Round-trip',
  ONE_WAY: 'One-way',
  MULTI_DESTINATION: 'Multi-destination'
};
const DateBox = ({
  title,
  date,
  isSmallerView,
  origin,
  dest
}) => {
  return React.createElement(Flex, {
    flexDirection: 'column',
    bg: 'background.light',
    justifyContent: 'center',
    textAlign: 'center',
    borderRadius: 'xl',
    style: {
      gap: '4px'
    },
    px: [3, null, null, null, 4],
    py: 2,
    width: [1, null, 'auto'],
    minWidth: isSmallerView ? [null, null, null, null, 135] : null
  }, React.createElement(NoWrapDateText, {
    fontSize: 0,
    color: 'text.light',
    bold: true
  }, title), React.createElement(NoWrapDateText, {
    textStyle: ['heading6', null, 'captionBold', null, isSmallerView ? 'captionBold' : 'heading6']
  }, date), React.createElement(Flex, {
    alignItems: 'center',
    justifyContent: 'center'
  }, React.createElement(Text, {
    textStyle: ['heading6', null, 'captionBold', null, isSmallerView ? 'captionBold' : 'heading6']
  }, origin), React.createElement(Box, {
    mx: 2,
    bg: 'text',
    height: '2px',
    width: '32px'
  }), React.createElement(Text, {
    textStyle: ['heading6', null, 'captionBold', null, isSmallerView ? 'captionBold' : 'heading6']
  }, dest)));
};
/** @public */
export default function AbandonedFlight({
  isSmallerView = false,
  tripType,
  dealType,
  heading,
  departDate,
  returnDate,
  departOriginAirportCode,
  departDestAirportCode,
  returnOriginAirportCode,
  returnDestAirportCode,
  numAdults = 2,
  buttonText,
  onClick,
  handleSubmitCTA,
  url
}) {
  const expressLogoImageSrc = `https://s1.pclncdn.com/design-assets/home/recent-activity/express-deals-logo-refresh.png`;
  function handleKeyDown(e) {
    if (handleSubmitCTA && e.key === 'Enter') {
      handleSubmitCTA(e);
    }
  }
  function handleOnClick(e) {
    e.stopPropagation();
    onClick(e);
  }
  return React.createElement(StyledCard, {
    "data-testid": 'abandoned-flight-card',
    bg: 'background.lightest',
    borderRadius: 'xl',
    boxShadowSize: 'md',
    p: 3,
    borderWidth: 0,
    role: 'link',
    tabIndex: 0,
    onClick: onClick,
    onKeyDown: handleKeyDown
  }, React.createElement(StyledFlex, {
    flexDirection: ['column', null, 'row'],
    justifyContent: 'space-between'
  }, React.createElement(Flex, {
    alignItems: 'center'
  }, React.createElement(IconWrapper, {
    width: 98,
    height: 50,
    justifyContent: 'center'
  }, React.createElement(FlightsOutline, {
    size: '50px'
  })), React.createElement(Flex, {
    flexDirection: 'column',
    style: {
      gap: '4px'
    }
  }, dealType === 'EXPRESS' && React.createElement(Image, {
    width: 112,
    height: 16,
    alt: 'expressLogo',
    src: `${expressLogoImageSrc}?opto&auto=avif,webp`
  }), React.createElement(StyledTruncate, {
    textStyle: ['heading5', null, null, null, isSmallerView ? null : 'heading4'],
    width: 'auto'
  }, heading), React.createElement(Text, {
    textStyle: 'caption',
    color: 'text.light'
  }, `${tripTypeText[tripType]} flight`))), React.createElement(StyledFlex, {
    flexDirection: ['column', null, 'row']
  }, React.createElement(StyledFlex, null, React.createElement(DateBox, {
    title: 'DEPART',
    date: departDate,
    isSmallerView: isSmallerView,
    origin: departOriginAirportCode,
    dest: departDestAirportCode
  }), tripType !== 'ONE_WAY' && React.createElement(DateBox, {
    title: 'RETURN',
    date: returnDate,
    isSmallerView: isSmallerView,
    origin: returnOriginAirportCode,
    dest: returnDestAirportCode
  })), React.createElement(StyledFlex, {
    flexDirection: tripType === 'ONE_WAY' ? ['column', null, null, 'row'] : ['column', null, null, null, null, isSmallerView ? 'column' : 'row'],
    justifyContent: 'center'
  }, React.createElement(Flex, {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    bg: 'background.light',
    borderRadius: 'xl',
    py: [2, null, null, null, 1],
    px: [3, null, 2, 3, 4, 3]
  }, React.createElement(User, {
    size: 20,
    color: 'text.light'
  }), React.createElement(NoWrapText, {
    textStyle: 'caption',
    textAlign: 'center'
  }, `${numAdults} Adult${numAdults > 1 ? 's' : ''}, Economy`)), React.createElement(Link, {
    href: url,
    variation: 'fill',
    target: '_blank',
    size: ['medium', null, 'small', null, null, isSmallerView ? 'small' : 'medium'],
    onClick: handleOnClick,
    "aria-label": 'Continue Booking',
    title: 'Continue Booking',
    my: [0, null, null, 'auto']
  }, React.createElement(NoWrapText, null, buttonText || 'Continue Booking'))))));
}